// Date of birth: the age check at sign-up (owner, 2026-10-13).
//
// The server is the authority (backend/app/date_of_birth.py): it refuses an
// under-age sign-up whatever this file says. The checks here exist so a
// customer hears "you need to be at least 18" before submitting, not after.
// Keep MIN_ACCOUNT_AGE equal to the backend's.

export const MIN_ACCOUNT_AGE = 18;

// The oldest year offered. 110 years covers every living customer without
// a 126-entry list on a phone.
export const YEARS_OFFERED = 110;

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

// Today's date in Nairobi, as {year, month (1-12), day}. Age is counted on
// the Kenyan calendar, the same rule the server uses.
export function todayInKenya(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Nairobi", year: "numeric", month: "numeric", day: "numeric",
  }).formatToParts(now);
  const get = (t) => Number(parts.find((p) => p.type === t).value);
  return { year: get("year"), month: get("month"), day: get("day") };
}

const pad = (n) => String(n).padStart(2, "0");

// "1990-05-17", or null when the three parts do not make a real date
// (31 February, an empty select).
export function buildIsoDate(day, month, year) {
  const d = Number(day), m = Number(month), y = Number(year);
  if (!d || !m || !y) return null;
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return null;
  return `${y}-${pad(m)}-${pad(d)}`;
}

export function ageOn(iso, today = todayInKenya()) {
  const [y, m, d] = iso.split("-").map(Number);
  let age = today.year - y;
  if (today.month < m || (today.month === m && today.day < d)) age -= 1;
  return age;
}

// { iso } when the date can be sent, otherwise { error } with the message
// to show. Messages match the server's wording.
export function checkDateOfBirth(day, month, year, today = todayInKenya()) {
  if (!day && !month && !year) return { error: "Please enter your date of birth." };
  const iso = buildIsoDate(day, month, year);
  if (!iso) return { error: "Please choose a real date: day, month and year." };
  const todayIso = `${today.year}-${pad(today.month)}-${pad(today.day)}`;
  if (iso > todayIso) return { error: "Your date of birth can't be in the future." };
  if (ageOn(iso, today) < MIN_ACCOUNT_AGE) {
    return { error: `Sorry, you need to be at least ${MIN_ACCOUNT_AGE} to create an Emporia account.` };
  }
  return { iso };
}

// "17 May 1990"
export function formatDateOfBirth(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

// Profile, for an account created before dates of birth were collected: a
// real date, not in the future. No minimum age here: the account already
// exists, and the date then decides what age-restricted content it sees.
export function checkDateForProfile(day, month, year, today = todayInKenya()) {
  const iso = buildIsoDate(day, month, year);
  if (!iso) return { error: "Please choose a real date: day, month and year." };
  const todayIso = `${today.year}-${pad(today.month)}-${pad(today.day)}`;
  if (iso > todayIso) return { error: "Your date of birth can't be in the future." };
  return { iso };
}
